import { Action } from 'redux';
import { isType } from 'typescript-fsa';

import { initializeState } from 'src/utils/Initial';
import * as Actions from '../actions/imstat';
import { isMobile } from '../utils/MobileCheck';

export interface IImstate<T> {
	showType: Actions.ImstatShowTypes;
	data: T[];
	selectedColumns: number[];
	setDataAt?: Date;
}

/**
 * 実施状況のState型
 */
export interface IImstatState {
	readonly onGoingELearning: IImstate<Actions.IOnGoingELearning>;
	readonly onGoingGroupTraining: IImstate<Actions.IOnGoingGroupTraining>;
	readonly onGoingDistanceLearning: IImstate<
		Actions.IOnGoingDistanceLearning
	>;
	readonly onGoingCurriculum: IImstate<Actions.ICurriculum>;
	readonly resultELearning: IImstate<Actions.IResultELearning>;
	readonly resultGroupTraining: IImstate<Actions.IResultGroupTraining>;
	readonly resultDistanceLearning: IImstate<Actions.IResultDistanceLearning>;
	readonly resultCurriculum: IImstate<Actions.ICurriculum>;
	readonly changeApplication: IApplicationState;
	readonly cancelApplication: IApplicationCancelState;
	readonly actResultApplication: IActResultApplicationState;
	readonly lectureCancel: ILectureCancelState;
	readonly trainingProgress: Actions.TrainingProgressState;
	readonly applicationStatus: IApplicationStatusState;
	readonly questionnaireHistory: IQuestionnaireHistoryState;
	readonly curriculumContent: ICurriculmContentState;
	readonly actResultReference: Actions.IActResultReference;
	readonly fetchDefaultDisplayFormatAt?: Date;
	readonly careerDetailDialog: Actions.ICareerDetailDialog;
	readonly cancleStayMealdata: Actions.ICancelStayMeal[];
}

/**
 * 受講申請のState型
 */
export interface IApplicationState {
	readonly data: Actions.IApplication;
	readonly dialogOpen: boolean;
}

/**
 * 受講申請取消のState型
 */
export interface IApplicationCancelState {
	readonly dialogOpen: boolean;
}

/**
 * 実績申請のState型
 */
export interface IActResultApplicationState {
	readonly data: Actions.IActResultApplication;
	readonly backup: Actions.IActResultApplication;
	readonly openSaveSuccessDialog: boolean;
	readonly openApplySuccessDialog: boolean;
}

/**
 * 受講取消のState型
 */
export interface ILectureCancelState {
	readonly data: Actions.IApplication;
	readonly dialogOpen: boolean;
}

/** 申請内容のState型 */
export interface IApplicationStatusState {
	readonly data: Actions.IApplicationStatus;
}

/** テストアンケート履歴のState型 */
export interface IQuestionnaireHistoryState {
	readonly data: Actions.IQuestionnaireSummary[];
}

/** カリキュラム内容のState型 */
export interface ICurriculmContentState {
	readonly data: Actions.ICurriculmContent[];
	readonly selectedColumns: number[];
}

const initialShowType = isMobile()
	? Actions.ImstatShowTypes.IMSTAT_SHOW_TYPE_CARD
	: Actions.ImstatShowTypes.IMSTAT_SHOW_TYPE_TABLE;

/**
 * 実施状況のState初期値
 */
const initialState: IImstatState = {
	actResultApplication: {
		backup: {},
		data: {},
		openApplySuccessDialog: false,
		openSaveSuccessDialog: false,
	},
	actResultReference: {
		careerTitle: '',
		className: '',
		comment: '',
		datelist: [],
		endDate: '',
		endDateODT: '',
		lecturerComment: '',
		managementCode: '',
		result: '',
		scheduleId: '',
		startDate: '',
		startDateODT: '',
		status: '',
		statusID: '',
		studentCareerCode: '',
		subStatus: '',
		subStatusID: '',
	},
	applicationStatus: {
		data: {},
	},
	cancelApplication: {
		dialogOpen: false,
	},
	cancleStayMealdata: [],
	careerDetailDialog: {
		apllType: '',
		careerID: '',
		classID: '',
		curriculumFlg: '0',
		setApplicationStatusDialog: false,
		setOpenCareerDetailDialog: false,
		setOpenCurriculumDetailDialog: false,
	},
	changeApplication: {
		data: {},
		dialogOpen: false,
	},
	curriculumContent: {
		data: [],
		selectedColumns: [0, 1, 2, 3, 4, 5, 6, 9, 13],
	},
	lectureCancel: {
		data: {},
		dialogOpen: false,
	},
	onGoingCurriculum: {
		data: [],
		selectedColumns: [0, 1, 2, 4, 5, 6],
		showType: initialShowType,
	},
	onGoingDistanceLearning: {
		data: [],
		selectedColumns: [0, 1, 2, 3, 5, 9, 11, 12, 13],
		showType: initialShowType,
	},
	onGoingELearning: {
		data: [],
		selectedColumns: [0, 1, 2, 4, 6, 7, 8, 9, 10, 11, 12],
		showType: initialShowType,
	},
	onGoingGroupTraining: {
		data: [],
		selectedColumns: [0, 1, 3, 4, 5, 6, 13, 14, 15],
		showType: initialShowType,
	},
	questionnaireHistory: {
		data: [],
	},
	resultCurriculum: {
		data: [],
		selectedColumns: [0, 1, 2, 4, 5, 6, 7],
		showType: initialShowType,
	},
	resultDistanceLearning: {
		data: [],
		selectedColumns: [0, 1, 2, 3, 5, 6, 10, 11, 12, 13],
		showType: initialShowType,
	},
	resultELearning: {
		data: [],
		selectedColumns: [0, 1, 2, 4, 5, 6, 7, 8, 10, 11],
		showType: initialShowType,
	},
	resultGroupTraining: {
		data: [],
		selectedColumns: [0, 1, 2, 3, 5, 7, 8, 15, 16],
		showType: initialShowType,
	},
	trainingProgress: 'none',
};

/**
 * 実施状況用reducer
 * @param state State
 * @param action アクション
 */
const imstat = (state: IImstatState, action: Action) => {
	if (!state) {
		// State初期化処理
		state = initialState;
		state = initializeState(state);
	}
	if (isType(action, Actions.setImstatOnGoingELearningShowType)) {
		return {
			...state,
			onGoingELearning: {
				...state.onGoingELearning,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingELearningData)) {
		return {
			...state,
			onGoingELearning: {
				...state.onGoingELearning,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingELearningSelectedColumns)) {
		return {
			...state,
			onGoingELearning: {
				...state.onGoingELearning,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingGroupTrainingShowType)) {
		return {
			...state,
			onGoingGroupTraining: {
				...state.onGoingGroupTraining,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingGroupTrainingData)) {
		return {
			...state,
			onGoingGroupTraining: {
				...state.onGoingGroupTraining,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingGroupTrainingSelectedColumns)) {
		return {
			...state,
			onGoingGroupTraining: {
				...state.onGoingGroupTraining,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingDistanceLearningShowType)) {
		return {
			...state,
			onGoingDistanceLearning: {
				...state.onGoingDistanceLearning,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingDistanceLearningData)) {
		return {
			...state,
			onGoingDistanceLearning: {
				...state.onGoingDistanceLearning,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (
		isType(action, Actions.setImstatOnGoingDistanceLearningSelectedColumns)
	) {
		return {
			...state,
			onGoingDistanceLearning: {
				...state.onGoingDistanceLearning,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingCurriculumShowType)) {
		return {
			...state,
			onGoingCurriculum: {
				...state.onGoingCurriculum,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingCurriculumData)) {
		return {
			...state,
			onGoingCurriculum: {
				...state.onGoingCurriculum,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (isType(action, Actions.setImstatOnGoingCurriculumSelectedColumns)) {
		return {
			...state,
			onGoingCurriculum: {
				...state.onGoingCurriculum,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setImstatResultELearningShowType)) {
		return {
			...state,
			resultELearning: {
				...state.resultELearning,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatResultELearningData)) {
		return {
			...state,
			resultELearning: {
				...state.resultELearning,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (isType(action, Actions.setImstatResultELearningSelectedColumns)) {
		return {
			...state,
			resultELearning: {
				...state.resultELearning,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setImstatResultGroupTrainingShowType)) {
		return {
			...state,
			resultGroupTraining: {
				...state.resultGroupTraining,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatResultGroupTrainingData)) {
		return {
			...state,
			resultGroupTraining: {
				...state.resultGroupTraining,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (isType(action, Actions.setImstatResultGroupTrainingSelectedColumns)) {
		return {
			...state,
			resultGroupTraining: {
				...state.resultGroupTraining,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setImstatResultDistanceLearningShowType)) {
		return {
			...state,
			resultDistanceLearning: {
				...state.resultDistanceLearning,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatResultDistanceLearningData)) {
		return {
			...state,
			resultDistanceLearning: {
				...state.resultDistanceLearning,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (
		isType(action, Actions.setImstatResultDistanceLearningSelectedColumns)
	) {
		return {
			...state,
			resultDistanceLearning: {
				...state.resultDistanceLearning,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setImstatResultCurriculumShowType)) {
		return {
			...state,
			resultCurriculum: {
				...state.resultCurriculum,
				showType: action.payload.showType,
			},
		};
	}

	if (isType(action, Actions.setImstatResultCurriculumData)) {
		return {
			...state,
			resultCurriculum: {
				...state.resultCurriculum,
				data: action.payload.data,
				setDataAt: new Date(),
			},
		};
	}

	if (isType(action, Actions.setImstatResultCurriculumSelectedColumns)) {
		return {
			...state,
			resultCurriculum: {
				...state.resultCurriculum,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setChangeApplicationData)) {
		return {
			...state,
			changeApplication: {
				...state.changeApplication,
				data: action.payload.data,
			},
		};
	}

	if (isType(action, Actions.showChangeApplicationDialog)) {
		return {
			...state,
			changeApplication: {
				...state.changeApplication,
				dialogOpen: action.payload.dialogOpen,
			},
		};
	}

	if (isType(action, Actions.showCancelApplicationDialog)) {
		return {
			...state,
			cancelApplication: {
				...state.cancelApplication,
				dialogOpen: action.payload.dialogOpen,
			},
		};
	}

	if (isType(action, Actions.setActResultApplicationData)) {
		return {
			...state,
			actResultApplication: {
				...state.actResultApplication,
				backup: action.payload.backup
					? action.payload.data
					: state.actResultApplication.backup,
				data: action.payload.data,
			},
		};
	}

	if (isType(action, Actions.showActResultApplicationDialog)) {
		return {
			...state,
			actResultApplication: {
				...state.actResultApplication,
				openApplySuccessDialog: action.payload.applySuccessDialogOpen,
				openSaveSuccessDialog: action.payload.saveSuccessDialogOpen,
			},
		};
	}

	if (isType(action, Actions.setLectureCancelData)) {
		return {
			...state,
			lectureCancel: {
				...state.lectureCancel,
				...action.payload,
			},
		};
	}

	if (isType(action, Actions.showLectureCancelDialog)) {
		return {
			...state,
			lectureCancel: {
				...state.lectureCancel,
				...action.payload,
			},
		};
	}

	if (isType(action, Actions.setTrainingProgress)) {
		return {
			...state,
			trainingProgress: action.payload.trainingProgress,
		};
	}

	if (isType(action, Actions.setApplicationStatusData)) {
		return {
			...state,
			applicationStatus: {
				...state.applicationStatus,
				...action.payload,
			},
		};
	}

	if (isType(action, Actions.setQuestionnaireHistoryData)) {
		return {
			...state,
			questionnaireHistory: {
				...state.questionnaireHistory,
				...action.payload,
			},
		};
	}

	if (isType(action, Actions.setCurriculmContentData)) {
		return {
			...state,
			curriculumContent: {
				...state.curriculumContent,
				data: action.payload.data,
			},
		};
	}

	if (isType(action, Actions.setCurriculmContentSelectedColumns)) {
		return {
			...state,
			curriculumContent: {
				...state.curriculumContent,
				selectedColumns: action.payload.selectedColumns,
			},
		};
	}

	if (isType(action, Actions.setActResultReferenceData)) {
		return {
			...state,
			actResultReference: action.payload.data,
		};
	}

	if (isType(action, Actions.resetActResultReferenceData)) {
		return {
			...state,
			actResultReference: initialState.actResultReference,
		};
	}

	if (isType(action, Actions.setImstatDefaultDisplaySettings)) {
		const newState = { ...state };
		const format =
			action.payload.defaultDisplayFormat === '0'
				? Actions.ImstatShowTypes.IMSTAT_SHOW_TYPE_CARD
				: Actions.ImstatShowTypes.IMSTAT_SHOW_TYPE_TABLE;
		newState.onGoingELearning.showType = format;
		newState.onGoingGroupTraining.showType = format;
		newState.onGoingDistanceLearning.showType = format;
		newState.onGoingCurriculum.showType = format;
		newState.resultELearning.showType = format;
		newState.resultGroupTraining.showType = format;
		newState.resultDistanceLearning.showType = format;
		newState.resultCurriculum.showType = format;

		return newState;
	}

	if (isType(action, Actions.clearImstat)) {
		return initialState;
	}

	if (isType(action, Actions.setCareerDetailDialog)) {
		return {
			...state,
			careerDetailDialog: action.payload.CareerDetailDialog,
		};
	}

	if (isType(action, Actions.setCancelStayMealData)) {
		return {
			...state,
			cancleStayMealdata: action.payload.cancelStayMealData,
		};
	}

	return state;
};

export default imstat;



// WEBPACK FOOTER //
// ./src/reducers/imstat.ts